import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, BookOpen, CircleDollarSign, Clock, FileWarning,
  HandCoins, Inbox, RefreshCw, TrendingUp, Users, Wallet,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

/**
 * Reimbursement dashboard.
 *
 * Written to answer questions rather than count rows. The figure that matters
 * most is not how many claims exist — it is how much money volunteers are
 * currently carrying personally, and how long they have been carrying it. A
 * dashboard of totals nobody acts on is decoration.
 */

const money = (v) => `$${v ?? "0.00"}`;
const claims = (n) => `${n || 0} claim${n === 1 ? "" : "s"}`;

function Kpi({ label, value, sub, icon: Icon, tone = "primary", onClick, action, testId }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Card className={onClick
      ? "hover:border-primary/60 hover:shadow-md focus-within:border-primary/60 transition-all cursor-pointer"
      : ""}>
      <CardContent className="p-0">
        <Wrapper
          {...(onClick ? { onClick, type: "button", "aria-label": `${label}: ${action || "open"}` } : {})}
          className="p-4 w-full text-left flex items-start justify-between gap-3 rounded-xl
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-testid={testId}
        >
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold text-foreground mt-1.5 truncate">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
            {/* A number that opens a page must say so; a card that just looks
                like a card gets ignored. */}
            {onClick && (
              <p className="text-xs font-medium text-primary mt-2 flex items-center gap-1">
                {action || "Open"}<ArrowRight className="h-3 w-3" aria-hidden="true" />
              </p>
            )}
          </div>
          <span className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0
                            bg-${tone}/10 text-${tone}`}
                style={{ backgroundColor: `hsl(var(--${tone}) / 0.1)`,
                         color: `hsl(var(--${tone}))` }}>
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
        </Wrapper>
      </CardContent>
    </Card>
  );
}

function Breakdown({ title, rows, icon: Icon, emptyText, testId }) {
  const max = Math.max(...rows.map((r) => r.cents), 1);
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5 space-y-3">
        <h2 className="font-semibold text-foreground flex items-center gap-1.5 text-sm">
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {title}
        </h2>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-foreground truncate">
                  {row.name || row.title || row.id}
                </span>
                <span className="text-sm font-semibold text-foreground shrink-0">
                  {money(row.amount)}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary"
                     style={{ width: `${Math.round((row.cents / max) * 100)}%` }} />
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default function ClaimsDashboardPage() {
  const navigate = useNavigate();
  const { authAxios, isAdmin, isSubAdmin } = useAuth();
  const canPay = isAdmin || isSubAdmin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get("/claims/analytics");
      setData(res.data);
    } catch {
      toast.error("Could not load the reimbursement dashboard");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-24 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const attention = data?.needs_attention || {};
  const flagged = (attention.no_receipt || 0) + (attention.over_threshold || 0)
    + (attention.queried || 0);

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Reimbursements</h1>
          <p className="text-sm text-muted-foreground">
            What volunteers are owed, and where it is in the process
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5"
                  onClick={() => navigate("/claims/process")}
                  data-testid="dash-process">
            <BookOpen className="h-4 w-4" aria-hidden="true" />
            How it works
          </Button>
          <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh"
                  data-testid="dash-refresh">
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* The lead figure is what people are personally carrying, not a count. */}
      <Card className="border-primary/40 bg-primary/5" data-testid="dash-outstanding">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Currently owed to volunteers
            </p>
            <p className="text-4xl font-bold text-foreground mt-1">
              {money(data?.outstanding?.total)}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              across {data?.outstanding?.count || 0} claim
              {data?.outstanding?.count === 1 ? "" : "s"}
              {data?.outstanding?.oldest_days
                ? ` · oldest waiting ${data.outstanding.oldest_days} days`
                : ""}
            </p>
          </div>
          <HandCoins className="h-12 w-12 text-primary/70" aria-hidden="true" />
        </CardContent>
      </Card>

      {data?.outstanding?.over_14_days > 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="dash-waiting-long">
          <CardContent className="p-4 flex items-start gap-2">
            <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-sm text-amber-700 dark:text-amber-500">
              <strong>{data.outstanding.over_14_days}</strong> claim
              {data.outstanding.over_14_days === 1 ? " has" : "s have"} been waiting more than
              two weeks. People are funding these themselves in the meantime.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Awaiting review" value={money(data?.awaiting_review?.total)}
             sub={claims(data?.awaiting_review?.count)} icon={Inbox}
             tone="amber" onClick={() => navigate("/claims/review")}
             action="Review claims" testId="kpi-awaiting" />
        <Kpi label="Approved, unpaid" value={money(data?.approved_unpaid?.total)}
             sub={claims(data?.approved_unpaid?.count)} icon={Wallet}
             tone="blue" onClick={canPay ? () => navigate("/claims/payments") : undefined}
             action="Run a payment" testId="kpi-approved" />
        <Kpi label="Paid to date" value={money(data?.paid?.total)}
             sub={claims(data?.paid?.count)} icon={CircleDollarSign}
             tone="emerald" testId="kpi-paid" />
        <Kpi label="Typical turnaround"
             value={data?.median_turnaround_days == null
               ? "—" : `${data.median_turnaround_days} days`}
             sub="submitted to paid" icon={TrendingUp} tone="violet"
             testId="kpi-turnaround" />
      </div>

      {flagged > 0 && (
        <Card data-testid="dash-attention">
          <CardContent className="p-5 space-y-2">
            <h2 className="font-semibold text-foreground flex items-center gap-1.5 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
              Needs a closer look
            </h2>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {attention.no_receipt > 0 && (
                <span className="flex items-center gap-1.5">
                  <FileWarning className="h-3.5 w-3.5" aria-hidden="true" />
                  {attention.no_receipt} without a receipt
                </span>
              )}
              {attention.over_threshold > 0 && (
                <span>{attention.over_threshold} above the pre-approval limit</span>
              )}
              {attention.queried > 0 && (
                <span>{attention.queried} waiting on a reply</span>
              )}
            </div>
            <Button variant="outline" size="sm" className="h-10 gap-1.5 mt-1"
                    onClick={() => navigate("/claims/review")}
                    data-testid="dash-goto-review">
              Go to review
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Breakdown title="Who is out of pocket" rows={data?.out_of_pocket || []}
                   icon={Users} testId="dash-out-of-pocket"
                   emptyText="Nobody is currently owed anything." />
        <Breakdown title="Spend by category" rows={data?.by_category || []}
                   icon={CircleDollarSign} testId="dash-by-category"
                   emptyText="No approved spend yet." />
        <Breakdown title="Spend by event" rows={data?.by_event || []}
                   icon={Wallet} testId="dash-by-event"
                   emptyText="No approved spend yet." />
        <Breakdown title="Most frequent claimants" rows={data?.by_person || []}
                   icon={Users} testId="dash-by-person"
                   emptyText="No claims yet." />
      </div>

      {data?.by_month?.length > 1 && (
        <Card data-testid="dash-by-month">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-semibold text-foreground text-sm">Reimbursements by month</h2>
            <div className="flex items-end gap-2 h-28" aria-hidden="true">
              {data.by_month.map((m) => {
                const max = Math.max(...data.by_month.map((x) => x.cents), 1);
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t bg-primary/70"
                         style={{ height: `${Math.max(4, (m.cents / max) * 100)}%` }} />
                    <span className="text-[10px] text-muted-foreground">
                      {m.month.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.by_month[0].month} to {data.by_month[data.by_month.length - 1].month}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
